<script lang="ts" setup>
import { QBadge } from 'quasar'
import { computed } from 'vue'
import { Color } from '@previo/codebooks'
import { PBadgeColor } from './types'

const MAX_NUMBER = 99

type Props = {
  text?: string | number | null
  color?: PBadgeColor
  floating?: boolean
  dot?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  text: null,
  color: PBadgeColor.NEGATIVE,
  floating: false,
  dot: false,
})

const displayText = computed<string | number | null>(() => {
  if (typeof props.text === 'number' && props.text > MAX_NUMBER) {
    return `${MAX_NUMBER}+`
  }

  return props.text
})

const bgColor = computed<Color.INFO_STATE | Color.POSITIVE | Color.WARNING | Color.NEGATIVE>(() => {
  switch (props.color) {
    case PBadgeColor.INFO:
      return Color.INFO_STATE
    case PBadgeColor.POSITIVE:
      return Color.POSITIVE
    case PBadgeColor.WARNING:
      return Color.WARNING
    case PBadgeColor.NEGATIVE:
      return Color.NEGATIVE

    default:
      throw 'Props color is not supported in PBadge'
  }
})

const textColor = computed<Color.WHITE | Color.BLACK>(() => {
  switch (props.color) {
    case PBadgeColor.INFO:
    case PBadgeColor.NEGATIVE:
    case PBadgeColor.POSITIVE:
      return Color.WHITE
    case PBadgeColor.WARNING:
      return Color.BLACK

    default:
      throw 'Props color is not supported in PBadge'
  }
})
</script>

<template>
  <q-badge
    class="ds-badge"
    :class="{ 'ds-badge-dot': dot }"
    :color="bgColor"
    :label="dot ? undefined : displayText ?? undefined"
    :floating="floating"
    :text-color="textColor"
  />
</template>

<style lang="scss" scoped>
.ds-badge {
  height: 16px;
  min-width: 16px;
  padding: 2px 4px;
  border-radius: 5px;
  font-size: 10px;
  justify-content: center;

  &-dot {
    height: 8px;
    width: 8px;
    min-width: 8px;
    min-height: 8px;
    padding: 0;
    border-radius: 50%;
  }
}
</style>
